import {Accordion, AccordionDetails, AccordionSummary, Typography} from '@mui/material';
import moment from 'moment';
import React, {useEffect, useMemo, useState} from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {useParams} from 'react-router-dom';
import parseHTML from 'html-react-parser';
import {OpenInNew} from '@mui/icons-material';
import {GetHandoverNotes} from '../../schema-types/GetHandoverNotes';
import {NoteFragment} from '../../schema-types/NoteFragment';
import {useHandoverNoteStyles} from './handoverNoteStyles';
import {getReviewReason} from './reviewReasonMap';
import {HandoverNoteFields} from '../../pages/HandoverNotesPage/HandoverNotesHelpers';
import {SaltusAny} from '../../utils/SaltusAny';
import theme from '../../theme/Theme';
import {Button} from '../../components/Button/Button';
import {useAccountContext} from '../../contexts/AccountContext';

export const PreviousNotes: React.FC<{
  allPreviousNotes: GetHandoverNotes['previousNotes'];
  page: HandoverNoteFields;
}> = ({allPreviousNotes, page}) => {
  const {accountId} = useAccountContext();
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const {reviewId} = useParams();

  const previousRelatedNotes = useMemo(() => {
    const relatedNotes: (NoteFragment & {reviewId: string; reviewType: string})[] = [];
    allPreviousNotes?.forEach((review): SaltusAny => {
      // anything that's not the current review is a previous review basically
      if (review.id === reviewId) {
        return;
      }

      review.notes?.forEach((note) => {
        if (note.displayName !== page) {
          return;
        }
        if ((note.body && note.body !== 'None') || note.reviewed) {
          relatedNotes.push({
            ...note,
            body: note.body && note.body !== 'None' ? note.body : 'No change',
            markupBody: note.markupBody,
            reviewId: review.id,
            reviewType: `${getReviewReason(review)?.text || review.reviewReason}`,
            dateCreated: review.dateBooked,
          });
        }
      });
    });
    return relatedNotes;
  }, [page, allPreviousNotes]);

  useEffect(() => {
    if (!previousRelatedNotes) {
      return;
    }
    for (const previousNote of previousRelatedNotes) {
      if (previousNote.displayName === page) {
        if (previousNote.body) {
          setOpen((x) => ({...x, [previousNote.id]: true}));
          return; // after the first non empty and relevant note, stop
        }
      }
    }
  }, [previousRelatedNotes]);

  const classes = useHandoverNoteStyles();
  if (!allPreviousNotes) {
    return (
      <div style={{textAlign: 'center', paddingTop: 10, fontWeight: 'bold'}}>
        Error fetching previous notes
      </div>
    );
  }
  if (!previousRelatedNotes?.length) {
    return (
      <div style={{textAlign: 'center', paddingTop: 10, fontWeight: 'bold'}}>
        No historical {page.toLocaleLowerCase()} notes
      </div>
    );
  }

  return (
    <div style={classes.previousNotesContainer}>
      <Typography sx={classes.noteTitle}>
        {previousRelatedNotes.length} {reviewId ? 'previous' : ''}{' '}
        {page.toLocaleLowerCase()} note
        {previousRelatedNotes.length === 1 ? '' : 's'}:
      </Typography>
      <div style={classes.previousNoteWrapper}>
        {previousRelatedNotes.map((note, i) => {
          return (
            <Accordion
              key={note.id}
              onChange={() => {
                const currentlyOpen = open[note.id];
                setOpen((x) => ({...x, [note.id]: !currentlyOpen}));
              }}
              expanded={open[note.id] || false}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    onClick={(e) => {
                      const currentlyOpen = open[note.id];
                      setOpen((x) => ({...x, [note.id]: !currentlyOpen}));

                      e.stopPropagation();
                    }}
                  />
                }
              >
                <div
                  style={{
                    fontWeight: 'bold',
                    fontSize: 16,
                    color: theme.palette.primary.main,
                    background: theme.palette.grey[200],
                    display: 'inline-block',
                    padding: `${theme.spacing(0.25)} ${theme.spacing(1)}`,
                    position: 'relative',
                    left: theme.spacing(-1),
                  }}
                >
                  {note.reviewType}{' '}
                  {note.dateCreated
                    ? moment.utc(note.dateCreated).format('DD.MM.YYYY')
                    : 'No date'}
                </div>
              </AccordionSummary>

              <AccordionDetails>
                <div style={{whiteSpace: 'pre-wrap'}}>
                  {note.markupBody ? parseHTML(note.markupBody) : note.body}
                </div>
                <Button
                  sx={{mt: 2}}
                  startIcon={<OpenInNew />}
                  eventName="Open review from note"
                  variant="outlined"
                  onClick={() =>
                    window.open(
                      `/account/${accountId}/review-journey/${note.reviewId}`,
                      '_blank'
                    )
                  }
                >
                  Open review (new tab)
                </Button>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </div>
    </div>
  );
};
